import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Campaign as CampaignIcon,
  PushPin as PushPinIcon,
  NewReleases as NewReleasesIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { mitteilungenApi } from '../services/api';
import type { Mitteilung, MitteilungenResponse, CreateMitteilungDto, UpdateMitteilungDto, MitteilungStatistik, ReleaseNote, CreateReleaseNoteDto } from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const getTypLabel = (typ: string): string => {
  switch (typ) {
    case 'ankuendigung': return 'Ankündigung';
    case 'wartung': return 'Wartung';
    case 'release_note': return 'Release';
    case 'info': return 'Info';
    default: return typ;
  }
};

const getPriorityColor = (prioritaet: string): 'default' | 'primary' | 'warning' | 'error' => {
  switch (prioritaet) {
    case 'kritisch': return 'error';
    case 'hoch': return 'warning';
    case 'normal': return 'primary';
    default: return 'default';
  }
};

const getCategoryLabel = (kategorie: string): string => {
  switch (kategorie) {
    case 'neu': return 'Neu';
    case 'verbessert': return 'Verbessert';
    case 'bugfix': return 'Bugfix';
    case 'sicherheit': return 'Sicherheit';
    case 'entfernt': return 'Entfernt';
    default: return kategorie;
  }
};

const getCategoryColor = (kategorie: string): string => {
  switch (kategorie) {
    case 'neu': return '#4caf50';
    case 'verbessert': return '#2196f3';
    case 'bugfix': return '#ff9800';
    case 'sicherheit': return '#f44336';
    case 'entfernt': return '#9e9e9e';
    default: return '#757575';
  }
};

const MitteilungenVerwaltung: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [mitteilungen, setMitteilungen] = useState<Mitteilung[]>([]);
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [statistik, setStatistik] = useState<MitteilungStatistik | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mitteilung Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMitteilung, setEditingMitteilung] = useState<Mitteilung | null>(null);
  const [formData, setFormData] = useState<CreateMitteilungDto>({
    titel: '',
    inhalt: '',
    typ: 'info',
    prioritaet: 'normal',
    zielgruppe: 'alle',
    istAktiv: true,
    istAngepinnt: false,
  });

  // Release Note Dialog
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [editingReleaseNote, setEditingReleaseNote] = useState<ReleaseNote | null>(null);
  const [releaseFormData, setReleaseFormData] = useState<CreateReleaseNoteDto>({
    version: '',
    titel: '',
    beschreibung: '',
    kategorie: 'neu',
    istVeroeffentlicht: true,
    reihenfolge: 0,
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (tabValue === 0) {
        const [mitteilungenRes, statistikRes] = await Promise.all([
          mitteilungenApi.getAlleAdmin({ page: 1, pageSize: 100 }),
          mitteilungenApi.getStatistik(),
        ]);
        setMitteilungen((mitteilungenRes.data as MitteilungenResponse).mitteilungen || []);
        setStatistik(statistikRes.data);
      } else if (tabValue === 1) {
        const response = await mitteilungenApi.getAlleReleaseNotes();
        setReleaseNotes(response.data || []);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Mitteilung Handlers
  const handleOpenDialog = (mitteilung?: Mitteilung) => {
    if (mitteilung) {
      setEditingMitteilung(mitteilung);
      setFormData({
        titel: mitteilung.titel,
        inhalt: mitteilung.inhalt,
        typ: mitteilung.typ,
        prioritaet: mitteilung.prioritaet,
        zielgruppe: mitteilung.zielgruppe,
        gueltigVon: mitteilung.gueltigVon?.split('T')[0],
        gueltigBis: mitteilung.gueltigBis?.split('T')[0],
        istAktiv: mitteilung.istAktiv,
        istAngepinnt: mitteilung.istAngepinnt,
      });
    } else {
      setEditingMitteilung(null);
      setFormData({
        titel: '',
        inhalt: '',
        typ: 'info',
        prioritaet: 'normal',
        zielgruppe: 'alle',
        istAktiv: true,
        istAngepinnt: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMitteilung(null);
  };

  const handleSaveMitteilung = async () => {
    try {
      if (editingMitteilung) {
        await mitteilungenApi.update(editingMitteilung.mitteilungID, formData as UpdateMitteilungDto);
      } else {
        await mitteilungenApi.create(formData);
      }
      await loadData();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'Mitteilung erfolgreich gespeichert',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  const handleDeleteMitteilung = async (id: number) => {
    if (!window.confirm('Mitteilung wirklich löschen?')) return;
    try {
      await mitteilungenApi.delete(id);
      await loadData();
      setSnackbar({
        open: true,
        message: 'Mitteilung erfolgreich gelöscht',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  // Release Note Handlers
  const handleOpenReleaseDialog = (releaseNote?: ReleaseNote) => {
    if (releaseNote) {
      setEditingReleaseNote(releaseNote);
      setReleaseFormData({
        version: releaseNote.version,
        titel: releaseNote.titel,
        beschreibung: releaseNote.beschreibung || '',
        kategorie: releaseNote.kategorie,
        veroeffentlichtAm: releaseNote.veroeffentlichtAm?.split('T')[0],
        istVeroeffentlicht: releaseNote.istVeroeffentlicht,
        reihenfolge: releaseNote.reihenfolge,
      });
    } else {
      setEditingReleaseNote(null);
      setReleaseFormData({
        version: '',
        titel: '',
        beschreibung: '',
        kategorie: 'neu',
        istVeroeffentlicht: true,
        reihenfolge: 0,
      });
    }
    setReleaseDialogOpen(true);
  };

  const handleCloseReleaseDialog = () => {
    setReleaseDialogOpen(false);
    setEditingReleaseNote(null);
  };

  const handleSaveReleaseNote = async () => {
    try {
      if (editingReleaseNote) {
        await mitteilungenApi.updateReleaseNote(editingReleaseNote.releaseNoteID, releaseFormData);
      } else {
        await mitteilungenApi.createReleaseNote(releaseFormData);
      }
      await loadData();
      handleCloseReleaseDialog();
      setSnackbar({
        open: true,
        message: 'Release-Note erfolgreich gespeichert',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  const handleDeleteReleaseNote = async (id: number) => {
    if (!window.confirm('Release-Note wirklich löschen?')) return;
    try {
      await mitteilungenApi.deleteReleaseNote(id);
      await loadData();
      setSnackbar({
        open: true,
        message: 'Release-Note erfolgreich gelöscht',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <CampaignIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Mitteilungen-Verwaltung</Typography>
        </Box>
        <Box display="flex" gap={1}>
          {tabValue === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Neue Mitteilung
            </Button>
          )}
          {tabValue === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenReleaseDialog()}
            >
              Neue Release-Note
            </Button>
          )}
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistik Cards */}
      {statistik && tabValue === 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CampaignIcon color="primary" />
                  <Box>
                    <Typography variant="h5">{statistik.anzahlMitteilungen}</Typography>
                    <Typography variant="caption" color="textSecondary">Gesamt</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="h5">{statistik.anzahlAktiv}</Typography>
                    <Typography variant="caption" color="textSecondary">Aktiv</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon color="info" />
                  <Box>
                    <Typography variant="h5">{statistik.anzahlGeplant}</Typography>
                    <Typography variant="caption" color="textSecondary">Geplant</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <AssessmentIcon color="warning" />
                  <Box>
                    <Typography variant="h5">{Math.round(statistik.durchschnittlicheLeseRate * 100)}%</Typography>
                    <Typography variant="caption" color="textSecondary">Leserate</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CampaignIcon fontSize="small" />
                Mitteilungen
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon fontSize="small" />
                Release-Notes
              </Box>
            }
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Titel</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Priorität</TableCell>
                  <TableCell>Zielgruppe</TableCell>
                  <TableCell>Gültigkeit</TableCell>
                  <TableCell>Leserate</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mitteilungen.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">
                        Keine Mitteilungen vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mitteilungen.map((mitteilung) => (
                    <TableRow key={mitteilung.mitteilungID} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {mitteilung.istAktiv ? (
                            <Chip label="Aktiv" size="small" color="success" />
                          ) : (
                            <Chip label="Inaktiv" size="small" color="default" />
                          )}
                          {mitteilung.istAngepinnt && (
                            <Tooltip title="Angepinnt">
                              <PushPinIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mitteilung.titel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={getTypLabel(mitteilung.typ)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={mitteilung.prioritaet} size="small" color={getPriorityColor(mitteilung.prioritaet)} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mitteilung.zielgruppe === 'alle' ? 'Alle' : mitteilung.zielgruppe === 'rolle' ? 'Rollen' : 'Benutzer'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {mitteilung.gueltigVon || mitteilung.gueltigBis ? (
                          <Typography variant="caption">
                            {mitteilung.gueltigVon && new Date(mitteilung.gueltigVon).toLocaleDateString('de-DE')}
                            {mitteilung.gueltigVon && mitteilung.gueltigBis && ' - '}
                            {mitteilung.gueltigBis && new Date(mitteilung.gueltigBis).toLocaleDateString('de-DE')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary">Unbegrenzt</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${mitteilung.anzahlGelesen} von ${mitteilung.anzahlEmpfaenger} gelesen`}>
                          <Typography variant="body2">
                            {mitteilung.anzahlEmpfaenger > 0
                              ? Math.round((mitteilung.anzahlGelesen / mitteilung.anzahlEmpfaenger) * 100)
                              : 0}%
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(mitteilung.erstelltAm).toLocaleDateString('de-DE')}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          {mitteilung.erstelltVonName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => handleOpenDialog(mitteilung)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton size="small" onClick={() => handleDeleteMitteilung(mitteilung.mitteilungID)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Titel</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell>Veröffentlicht am</TableCell>
                  <TableCell>Reihenfolge</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {releaseNotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">
                        Keine Release-Notes vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  releaseNotes.map((note) => (
                    <TableRow key={note.releaseNoteID} hover>
                      <TableCell>
                        {note.istVeroeffentlicht ? (
                          <Chip label="Veröffentlicht" size="small" color="success" />
                        ) : (
                          <Chip label="Entwurf" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {note.version}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCategoryLabel(note.kategorie)}
                          size="small"
                          sx={{ bgcolor: getCategoryColor(note.kategorie), color: 'white' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {note.titel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {note.beschreibung || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(note.veroeffentlichtAm).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell>{note.reihenfolge}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => handleOpenReleaseDialog(note)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton size="small" onClick={() => handleDeleteReleaseNote(note.releaseNoteID)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Mitteilung Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMitteilung ? 'Mitteilung bearbeiten' : 'Neue Mitteilung'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Titel"
              fullWidth
              required
              value={formData.titel}
              onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
            />

            <TextField
              label="Inhalt"
              fullWidth
              required
              multiline
              rows={6}
              value={formData.inhalt}
              onChange={(e) => setFormData({ ...formData, inhalt: e.target.value })}
              helperText="HTML-Formatierung wird unterstützt"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    value={formData.typ}
                    label="Typ"
                    onChange={(e) => setFormData({ ...formData, typ: e.target.value })}
                  >
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="ankuendigung">Ankündigung</MenuItem>
                    <MenuItem value="wartung">Wartung</MenuItem>
                    <MenuItem value="release_note">Release-Note</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priorität</InputLabel>
                  <Select
                    value={formData.prioritaet}
                    label="Priorität"
                    onChange={(e) => setFormData({ ...formData, prioritaet: e.target.value })}
                  >
                    <MenuItem value="niedrig">Niedrig</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="hoch">Hoch</MenuItem>
                    <MenuItem value="kritisch">Kritisch</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Zielgruppe</InputLabel>
              <Select
                value={formData.zielgruppe}
                label="Zielgruppe"
                onChange={(e) => setFormData({ ...formData, zielgruppe: e.target.value })}
              >
                <MenuItem value="alle">Alle Benutzer</MenuItem>
                <MenuItem value="rolle">Bestimmte Rollen</MenuItem>
                <MenuItem value="benutzer">Bestimmte Benutzer</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Gültig von"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.gueltigVon || ''}
                  onChange={(e) => setFormData({ ...formData, gueltigVon: e.target.value || undefined })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Gültig bis"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.gueltigBis || ''}
                  onChange={(e) => setFormData({ ...formData, gueltigBis: e.target.value || undefined })}
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.istAktiv}
                    onChange={(e) => setFormData({ ...formData, istAktiv: e.target.checked })}
                  />
                }
                label="Aktiv"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.istAngepinnt}
                    onChange={(e) => setFormData({ ...formData, istAngepinnt: e.target.checked })}
                  />
                }
                label="Angepinnt (oben anzeigen)"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSaveMitteilung}
            variant="contained"
            disabled={!formData.titel || !formData.inhalt}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Note Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleCloseReleaseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingReleaseNote ? 'Release-Note bearbeiten' : 'Neue Release-Note'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Version"
                  fullWidth
                  required
                  placeholder="z.B. 1.2.0"
                  value={releaseFormData.version}
                  onChange={(e) => setReleaseFormData({ ...releaseFormData, version: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Kategorie</InputLabel>
                  <Select
                    value={releaseFormData.kategorie}
                    label="Kategorie"
                    onChange={(e) => setReleaseFormData({ ...releaseFormData, kategorie: e.target.value })}
                  >
                    <MenuItem value="neu">Neu</MenuItem>
                    <MenuItem value="verbessert">Verbessert</MenuItem>
                    <MenuItem value="bugfix">Bugfix</MenuItem>
                    <MenuItem value="sicherheit">Sicherheit</MenuItem>
                    <MenuItem value="entfernt">Entfernt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Titel"
              fullWidth
              required
              value={releaseFormData.titel}
              onChange={(e) => setReleaseFormData({ ...releaseFormData, titel: e.target.value })}
            />

            <TextField
              label="Beschreibung"
              fullWidth
              multiline
              rows={4}
              value={releaseFormData.beschreibung}
              onChange={(e) => setReleaseFormData({ ...releaseFormData, beschreibung: e.target.value })}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Veröffentlichungsdatum"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={releaseFormData.veroeffentlichtAm || ''}
                  onChange={(e) => setReleaseFormData({ ...releaseFormData, veroeffentlichtAm: e.target.value || undefined })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reihenfolge"
                  type="number"
                  fullWidth
                  value={releaseFormData.reihenfolge || 0}
                  onChange={(e) => setReleaseFormData({ ...releaseFormData, reihenfolge: parseInt(e.target.value) || 0 })}
                  helperText="Niedrigere Zahlen werden zuerst angezeigt"
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={releaseFormData.istVeroeffentlicht}
                  onChange={(e) => setReleaseFormData({ ...releaseFormData, istVeroeffentlicht: e.target.checked })}
                />
              }
              label="Veröffentlicht"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReleaseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSaveReleaseNote}
            variant="contained"
            disabled={!releaseFormData.version || !releaseFormData.titel}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MitteilungenVerwaltung;
